/**
 * @file payrollConstants.js
 * @description Centralised constants for the Payroll module.
 *
 * Exports:
 *  - PAYROLL_STATUS        — lifecycle states for a payroll batch
 *  - TEMPLATE_STATUS       — lifecycle states for a payroll template
 *  - CALCULATION_TYPE      — how a salary component is calculated
 *  - DEDUCTION_TYPE        — statutory / custom deduction identifiers
 *  - DEFAULT_EMPLOYEE_PF_PCT / DEFAULT_EMPLOYER_PF_PCT — PF contribution rates
 *  - DEFAULT_ESIC_EMPLOYEE_PCT / DEFAULT_ESIC_EMPLOYER_PCT / DEFAULT_ESIC_CEILING — ESIC rates & ceiling
 *  - DEFAULT_SAMPLE_GROSS  — sample gross salary used in template previews
 *  - MONTH_NAMES           — full month names (January … December)
 *  - MONTH_OPTIONS         — { label, value } pairs for month select dropdowns
 *  - INDIAN_STATES         — list of all Indian states and UTs
 *  - INDIAN_STATE_OPTIONS  — { label, value } pairs for state select dropdowns
 *  - DEFAULT_TDS_SLABS     — income-tax slabs for new tax regime FY 2024-25
 *  - PAYROLL_STRINGS       — all user-visible text (per DESIGN_STANDARDS.md §23)
 */

// ---------------------------------------------------------------------------
// Status enums
// ---------------------------------------------------------------------------

/** Lifecycle states for a payroll batch. */
export const PAYROLL_STATUS = {
  DRAFT: "Draft",
  APPROVED: "Approved",
  PAID: "Paid",
};

/** Lifecycle states for a payroll template. */
export const TEMPLATE_STATUS = {
  ACTIVE: "Active",
  INACTIVE: "Inactive",
};

// ---------------------------------------------------------------------------
// Calculation type enum
// ---------------------------------------------------------------------------

/** How a salary component amount is derived. */
export const CALCULATION_TYPE = {
  FIXED: "fixed",
  PERCENTAGE: "percentage",
};

// ---------------------------------------------------------------------------
// Deduction type enum
// ---------------------------------------------------------------------------

/** Identifiers for statutory and custom deduction components. */
export const DEDUCTION_TYPE = {
  PF: "pf",
  ESIC: "esic",
  PT: "pt",
  TDS: "tds",
  CUSTOM: "custom",
};

// ---------------------------------------------------------------------------
// Default statutory values
// ---------------------------------------------------------------------------

/** Employee PF contribution rate (% of basic). */
export const DEFAULT_EMPLOYEE_PF_PCT = 12;

/** Employer PF contribution rate (% of basic). */
export const DEFAULT_EMPLOYER_PF_PCT = 12;

/** Employee ESIC contribution rate (% of gross). */
export const DEFAULT_ESIC_EMPLOYEE_PCT = 0.75;

/** Employer ESIC contribution rate (% of gross). */
export const DEFAULT_ESIC_EMPLOYER_PCT = 3.25;

/** Monthly gross salary ceiling above which ESIC is not applicable (₹). */
export const DEFAULT_ESIC_CEILING = 21000;

/** Sample gross salary used in template component previews (₹). */
export const DEFAULT_SAMPLE_GROSS = 50000;

// ---------------------------------------------------------------------------
// Month names & options
// ---------------------------------------------------------------------------

/** Full month names in calendar order. */
export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Month options for select dropdowns.
 * Each entry has `label` (full name) and `value` (1-based month number).
 */
export const MONTH_OPTIONS = MONTH_NAMES.map((name, i) => ({
  label: name,
  value: i + 1,
}));

// ---------------------------------------------------------------------------
// Indian states list (for PT slab configuration)
// ---------------------------------------------------------------------------

/** All Indian states and Union Territories. */
export const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

/**
 * Indian state options for select dropdowns.
 * Each entry has `label` and `value` set to the state name.
 */
export const INDIAN_STATE_OPTIONS = INDIAN_STATES.map((s) => ({ label: s, value: s }));

// ---------------------------------------------------------------------------
// Default TDS slabs — new tax regime FY 2024-25
// ---------------------------------------------------------------------------

/**
 * Income-tax slabs for TDS calculation under the new tax regime (FY 2024-25).
 * `incomeTo: null` indicates the slab has no upper bound.
 *
 * @type {Array<{ incomeFrom: number, incomeTo: number|null, taxRatePercent: number }>}
 */
export const DEFAULT_TDS_SLABS = [
  { incomeFrom: 0, incomeTo: 400000, taxRatePercent: 0 },
  { incomeFrom: 400000, incomeTo: 800000, taxRatePercent: 5 },
  { incomeFrom: 800000, incomeTo: 1200000, taxRatePercent: 10 },
  { incomeFrom: 1200000, incomeTo: 1600000, taxRatePercent: 15 },
  { incomeFrom: 1600000, incomeTo: 2000000, taxRatePercent: 20 },
  { incomeFrom: 2000000, incomeTo: 2400000, taxRatePercent: 25 },
  { incomeFrom: 2400000, incomeTo: null, taxRatePercent: 30 },
];

// ---------------------------------------------------------------------------
// PAYROLL_STRINGS — all user-visible text (DESIGN_STANDARDS.md §23)
// ---------------------------------------------------------------------------

/**
 * All user-visible strings for the Payroll module.
 * No hardcoded text should appear in components — import from here instead.
 */
export const PAYROLL_STRINGS = {
  // Page titles
  TEMPLATES_TITLE: "Payroll Templates",
  TEMPLATES_SUBTITLE: "Create and manage reusable salary structures for your organisation",
  BUILDER_TITLE_NEW: "New Payroll Template",
  BUILDER_TITLE_EDIT: "Edit Payroll Template",
  RUN_TITLE: "Run Payroll",
  RUN_SUBTITLE: "Process monthly salaries for your team",
  HISTORY_TITLE: "Payroll History",
  HISTORY_SUBTITLE: "View and manage all past payroll runs",
  REPORTS_TITLE: "Payroll Reports",
  REPORTS_SUBTITLE: "Generate and download statutory and operational reports",
  SETTINGS_PT_TITLE: "Professional Tax Slabs",
  SETTINGS_PT_SUBTITLE: "Configure state-wise Professional Tax slabs",
  SETTINGS_TDS_TITLE: "TDS Slabs",
  SETTINGS_TDS_SUBTITLE: "Configure income tax slabs for TDS calculation",

  // Buttons
  BTN_NEW_TEMPLATE: "New Template",
  BTN_SAVE_DRAFT: "Save as Draft",
  BTN_SAVE_ACTIVATE: "Save & Activate",
  BTN_CLONE: "Clone",
  BTN_ACTIVATE: "Activate",
  BTN_DEACTIVATE: "Deactivate",
  BTN_DELETE: "Delete",
  BTN_EDIT: "Edit",
  BTN_RUN_PAYROLL: "Run Payroll",
  BTN_APPROVE: "Approve",
  BTN_MARK_PAID: "Mark as Paid",
  BTN_DOWNLOAD_PAYSLIP: "Download Payslip",
  BTN_DOWNLOAD_ALL: "Download All Payslips",
  BTN_ADD_COMPONENT: "Add Component",
  BTN_ADD_ROW: "Add Row",
  BTN_SAVE_SLABS: "Save Slabs",
  BTN_NEXT: "Next",
  BTN_BACK: "Back",
  BTN_CONFIRM: "Confirm",
  BTN_CANCEL: "Cancel",

  // Loading labels
  LOADING_SAVING: "Saving\u2026",
  LOADING_APPROVING: "Approving\u2026",
  LOADING_PROCESSING: "Processing\u2026",
  LOADING_GENERATING: "Generating\u2026",
  LOADING_DOWNLOADING: "Downloading\u2026",

  // Success messages
  SUCCESS_TEMPLATE_CREATED: "Payroll template created successfully",
  SUCCESS_TEMPLATE_UPDATED: "Payroll template updated successfully",
  SUCCESS_TEMPLATE_CLONED: "Template cloned successfully",
  SUCCESS_TEMPLATE_ACTIVATED: "Template activated successfully",
  SUCCESS_TEMPLATE_DEACTIVATED: "Template deactivated successfully",
  SUCCESS_TEMPLATE_DELETED: "Template deleted successfully",
  SUCCESS_BATCH_APPROVED: "Payroll batch approved successfully",
  SUCCESS_BATCH_PAID: "Payroll batch marked as paid",
  SUCCESS_SLABS_SAVED: "Slabs saved successfully",

  // Error messages
  ERROR_GENERIC: "Something went wrong. Please try again.",
  ERROR_TEMPLATE_NAME_REQUIRED: "Template name is required",
  ERROR_NO_EARNING_COMPONENTS:
    "At least one earning component is required to activate this template",
  ERROR_CIRCULAR_DEPENDENCY:
    "Circular dependency detected in earning components. Please check percentage-based components.",
  ERROR_DUPLICATE_BATCH: "A payroll batch for this month already exists.",
  ERROR_BATCH_PAID_LOCKED: "This payroll batch has been paid and cannot be modified.",
  ERROR_MISSING_ATTENDANCE: "Attendance data is missing for some employees.",
  ERROR_PT_STATE_NOT_CONFIGURED: "Professional Tax state not configured.",

  // Confirm modal messages
  CONFIRM_DEACTIVATE_TEMPLATE:
    "Deactivating this template will prevent it from being assigned to new roles. Existing payroll history will be preserved.",
  CONFIRM_DELETE_TEMPLATE:
    "This action cannot be undone. The template will be permanently deleted.",
  CONFIRM_EDIT_USED_TEMPLATE:
    "This template has been used in approved or paid payroll batches. Changes will only affect future payroll runs.",
  CONFIRM_REMOVE_ROLE_ASSIGNMENT:
    "Removing this assignment means employees in this role will have no payroll template for future runs.",

  // Wizard step names
  STEP_1_NAME: "Select Month",
  STEP_2_NAME: "Review Attendance",
  STEP_3_NAME: "Review Salaries",
  STEP_4_NAME: "Approve & Pay",

  // Report tab names
  REPORT_TAB_SALARY_REGISTER: "Salary Register",
  REPORT_TAB_PF: "PF Report",
  REPORT_TAB_TDS: "TDS Report",
  REPORT_TAB_BANK_TRANSFER: "Bank Transfer List",

  // Empty states
  EMPTY_TEMPLATES: "No payroll templates yet. Create your first template to get started.",
  EMPTY_BATCHES: "No payroll runs yet. Run your first payroll to see history here.",
  EMPTY_REPORTS: "Select a payroll batch to generate reports.",
  PAYSLIP_NOT_AVAILABLE: "Payslip not available for this month.",
};
